import React from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingCart, Plus, Minus } from 'lucide-react';
import { useCart } from '../hooks/useCart';
import { useProduct } from '../hooks/useProduct';
import { formatCurrency } from '../../../utils/format';

export default function ProductPage() {
  const { productId } = useParams();
  const { product, loading, error } = useProduct(productId);
  const { items, addItem, updateQuantity } = useCart();
  const cartItem = items.find(item => item?.id === productId);

  const handleAddToCart = () => {
    if (!product) return;

    if (!cartItem) {
      addItem({
        id: product.id,
        name: product.name,
        price: product.salePrice > 0 ? product.salePrice : product.price,
        quantity: 1,
        image: product.image,
        unit: product.unit
      });
    } else {
      updateQuantity(product.id, cartItem.quantity + 1);
    }
  };

  const handleDecreaseQuantity = () => {
    if (cartItem && cartItem.quantity > 1) {
      updateQuantity(productId!, cartItem.quantity - 1);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="aspect-square bg-gray-200 rounded-lg mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-4">Produto não encontrado</h2>
        <p className="text-gray-600">
          Desculpe, não conseguimos encontrar o produto que você está procurando.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {/* Product Image */}
      <div className="aspect-square">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover rounded-lg"
        />
      </div>

      {/* Product Info */}
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">{product.name}</h1>
          <div className="mt-4">
            {product.salePrice > 0 ? (
              <div className="space-y-1">
                <span className="text-2xl font-bold text-accent">
                  {formatCurrency(product.salePrice)}
                </span>
                <span className="ml-2 text-lg text-gray-500 line-through">
                  {formatCurrency(product.price)}
                </span>
              </div>
            ) : (
              <span className="text-2xl font-bold">
                {formatCurrency(product.price)}
              </span>
            )}
          </div>
        </div>

        <div className="prose max-w-none">
          <p>{product.description}</p>
        </div>

        <div className="pt-6 border-t">
          {product.stock === 0 ? (
            <div className="text-red-600 font-medium">Produto esgotado</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Quantidade</span>
                <div className="flex items-center space-x-4">
                  {!cartItem ? (
                    <button
                      onClick={handleAddToCart}
                      className="flex items-center justify-center space-x-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark"
                    >
                      <ShoppingCart className="h-5 w-5" />
                      <span>Adicionar ao Carrinho</span>
                    </button>
                  ) : (
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={handleDecreaseQuantity}
                        className="p-2 text-primary hover:bg-primary hover:text-white rounded-md transition-colors"
                      >
                        <Minus className="h-5 w-5" />
                      </button>
                      <span className="font-medium text-lg min-w-[2ch] text-center">
                        {cartItem.quantity}
                      </span>
                      <button
                        onClick={handleAddToCart}
                        className="p-2 text-primary hover:bg-primary hover:text-white rounded-md transition-colors"
                      >
                        <Plus className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="space-y-4 pt-6 border-t">
          <div>
            <span className="font-medium">Disponibilidade:</span>
            <span className={`ml-2 ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {product.stock > 0 ? 'Em estoque' : 'Esgotado'}
            </span>
          </div>
          <div>
            <span className="font-medium">Unidade:</span>
            <span className="ml-2">{product.unit}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
